import {ButtonActionProps, openDialog} from 'platform/components';
import {match, Pattern} from 'ts-pattern';

import {isEmpty} from 'ramda';

import {CustomerResponseBodyV2} from '@dms/api/customer';
import i18n from '@dms/i18n';

import {Nullish} from 'shared/web';

import {CallCustomerDialog} from '../components/CallCustomerDialog/CallCustomerDialog';
import {getCustomerCallPhoneNumbers} from '../utils/getCustomerCallPhoneNumbers';
import {useBrowserPhone} from './useBrowserPhone';
import {useCurrentTelephonyCall} from './useCurrentTelephonyCall';
import {useTelephonyCallAvailability} from './useTelephonyCallAvailability';

interface UseCallCustomerActionArgs {
  customerId: string;
  customer: CustomerResponseBodyV2 | Nullish;
  'data-testid': string;
}

const CALL_CUSTOMER_DIALOG_ID = 'callCustomerDialog';

/**
 * The header "Call" action of an entity that belongs to a customer. Returns null when telephony is
 * off for this user (flag or permission), and a disabled action with the reason as tooltip when
 * the customer has no number or the viewer is already on a call. Tenant readiness (licence, line,
 * the caller's mobile) is shown inside the dialog from `GET /telephony/call-options`.
 */
export function useCallCustomerAction(args: UseCallCustomerActionArgs): ButtonActionProps | null {
  const {isAllowed} = useTelephonyCallAvailability();
  const {isOnCall} = useCurrentTelephonyCall();
  const {phone} = useBrowserPhone();

  if (!isAllowed) {
    return null;
  }

  const hasPhoneNumber = !isEmpty(getCustomerCallPhoneNumbers(args.customer));

  const tooltip = match([hasPhoneNumber, isOnCall])
    .with([false, Pattern.boolean], () => i18n.t('page.telephony.call.customerHasNoPhone'))
    .with([true, true], () => i18n.t('page.telephony.call.callInProgressTooltip'))
    .with([true, false], () => i18n.t('page.telephony.call.tooltip'))
    .exhaustive();

  const handleClick = () => {
    // A token request that failed on a network blip gets another go before the dialog needs it.
    phone.retryIfUnavailable();

    openDialog(
      <CallCustomerDialog customerId={args.customerId} data-testid={args['data-testid']} />,
      {
        id: CALL_CUSTOMER_DIALOG_ID,
        title: i18n.t('page.telephony.call.title'),
        size: 'small',
      }
    );
  };

  return {
    type: 'button',
    variant: 'secondary',
    leftIcon: 'communication/call',
    title: i18n.t('general.actions.call'),
    tooltip,
    isDisabled: !hasPhoneNumber || isOnCall,
    onClick: handleClick,
    'data-testid': args['data-testid'],
  };
}
